import React, { useEffect, useState } from 'react';
import SystemDashboard from 'layouts/SystemDashboard';
import {
  Alert,
  Box,
  Breadcrumbs,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
} from '@mui/material';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { format } from 'date-fns-tz';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { getApp, saveApp, updateApp } from './AppService';

import AppItemList from './AppItemList';

const AppDetail = (): JSX.Element => {
  const { appid } = useParams();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  //const timeZone = 'Europe/London'; // UK time zone

  const validationSchema = yup.object({
    appName: yup.string().required('App Name is required.'),
    appCode: yup.string().required('App Code is required.'),
    versionNumber: yup.string().required('Version Number is required.'),
    buildNumber: yup.string().required('Build Number is required.'),
    status: yup.string().required('Status is required.'),
    startDate: yup.date().nullable().required('Start Date is required.'),
    endDate: yup
      .date()
      .nullable()
      .min(yup.ref('startDate'), 'End Date cannot be before Start Date')
      .required('End Date is required.'),
  });

  const formik = useFormik({
    initialValues: {
      appName: '',
      appDescription: '',
      appCode: '',
      versionNumber: '',
      buildNumber: '',
      status: '',
      startDate: '',
      endDate: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const app = {
        ...values,
        startDate: values.startDate
          ? new Date(values.startDate).toISOString()
          : null,
        endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
      };

      if (appid) {
        updateApp(parseInt(appid), app)
          .then(() => {
            setIsSaved(true);
            navigate('/system/dashboard/apps');
          })
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      } else {
        saveApp(app)
          .then(() => {
            setIsSaved(true);
            navigate('/system/dashboard/apps');
          })
          .catch((error) => setErrorMessage(extractErrorMessage(error)));
      }
    },
  });

  useEffect(() => {
    if (appid) {
      getApp(parseInt(appid))
        .then((response) => {
          formik.setValues({
            appName: response.data.appName,
            appDescription: response.data.appDescription,
            appCode: response.data.appCode,
            versionNumber: response.data.versionNumber,
            buildNumber: response.data.buildNumber,
            status: response.data.status,
            startDate: response.data.startDate
              ? formatDateForInput(response.data.startDate)
              : '',
            endDate: response.data.endDate
              ? formatDateForInput(response.data.endDate)
              : '',
          });
        })
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    }
  }, [appid]);

  // Helper function to format date to 'YYYY-MM-DDTHH:mm'
  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);

    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return ''; // Return empty string if the date is invalid
    }

    // Format date for input (YYYY-MM-DDTHH:mm)
    return date.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };

  return (
    <Container>
      <SystemDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              System Content
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/system/dashboard/apps"
            >
              Apps
            </Link>
            <Typography color="text.primary">App Detail</Typography>
          </Breadcrumbs>
        </Box>
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <Box marginBottom={4}>
          <Typography variant="h4">App Detail</Typography>
        </Box>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="App Name"
                  name="appName"
                  fullWidth
                  value={formik.values.appName}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.appName && Boolean(formik.errors.appName)
                  }
                  helperText={formik.touched.appName && formik.errors.appName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="App Code"
                  name="appCode"
                  fullWidth
                  value={formik.values.appCode}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.appCode && Boolean(formik.errors.appCode)
                  }
                  helperText={formik.touched.appCode && formik.errors.appCode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Version Number"
                  name="versionNumber"
                  fullWidth
                  value={formik.values.versionNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.versionNumber &&
                    Boolean(formik.errors.versionNumber)
                  }
                  helperText={
                    formik.touched.versionNumber && formik.errors.versionNumber
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Build Number"
                  name="buildNumber"
                  fullWidth
                  value={formik.values.buildNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.buildNumber &&
                    Boolean(formik.errors.buildNumber)
                  }
                  helperText={
                    formik.touched.buildNumber && formik.errors.buildNumber
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Status"
                  name="status"
                  fullWidth
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  error={formik.touched.status && Boolean(formik.errors.status)}
                  helperText={formik.touched.status && formik.errors.status}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  type="datetime-local"
                  value={formik.values.startDate || ''}
                  onChange={(e) =>
                    formik.setFieldValue('startDate', e.target.value)
                  }
                  fullWidth
                  error={
                    formik.touched.startDate && Boolean(formik.errors.startDate)
                  }
                  helperText={
                    formik.touched.startDate && formik.errors.startDate
                  }
                  InputLabelProps={{
                    shrink: true, // Ensures the label floats when there is a value
                  }}
                  InputProps={{
                    value: formatDateForInput(formik.values.startDate), // Properly format for display
                  }}
                  // style={{
                  //   visibility: formik.values.startDate ? 'visible' : 'hidden',
                  // }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Date"
                  type="datetime-local"
                  value={formik.values.endDate || ''}
                  onChange={(e) =>
                    formik.setFieldValue('endDate', e.target.value)
                  }
                  fullWidth
                  error={
                    formik.touched.endDate && Boolean(formik.errors.endDate)
                  }
                  helperText={formik.touched.endDate && formik.errors.endDate}
                  InputLabelProps={{
                    shrink: true, // Ensures the label floats when there is a value
                  }}
                  InputProps={{
                    value: formatDateForInput(formik.values.endDate), // Properly format for display
                  }}
                  // style={{
                  //   visibility: formik.values.startDate ? 'visible' : 'hidden',
                  // }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  marginBottom={4}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/system/dashboard/apps')}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained">
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </LocalizationProvider>
        <Box>
          {(appid || isSaved) && (
            <Box marginTop={4}>
              <AppItemList />
            </Box>
          )}
        </Box>
      </SystemDashboard>
    </Container>
  );
};

export default AppDetail;
